// Content for FAQ and Funding pages. Easy for Madelyn to edit.

export const FAQ_SECTIONS = [
  {
    id: "getting-started",
    title: "Getting started",
    items: [
      {
        q: "Can I refer myself?",
        a: "Yes. Anyone can refer to Navica Supports, participants, family members, advocates, plan managers, support workers, doctors, hospitals or other community services. Use the Make a Referral form on this site and we will be in touch.",
      },
      {
        q: "What happens after I send a referral?",
        a: "You will receive a confirmation by email within minutes. A Navica team member will reach out within 1 to 2 business days to arrange a no-obligation discovery call. We talk through what you need, check we are the right fit, and explain next steps in plain language.",
      },
      {
        q: "How quickly can you start?",
        a: "For Support Coordination and Recovery Coaching we can usually start within 1 to 2 weeks of receiving your referral and a signed service agreement. Urgent cases (hospital discharge, safety concerns, plan utilisation deadlines) are prioritised, let us know in your referral and we will move faster.",
      },
      {
        q: "Do you support participants outside Sydney?",
        a: "Yes. Our office is in Parramatta and we already service NSW, Queensland and Victoria (including Melbourne), plus remotely Australia-wide.",
      },
    ],
  },
  {
    id: "ndis-supports",
    title: "NDIS supports & service questions",
    items: [
      {
        q: "What's the difference between Support Coordination and Psychosocial Recovery Coaching (PSRC)?",
        a: "NDIS Support Coordination is about understanding your plan, finding providers, and getting things working. Psychosocial Recovery Coaching (PSRC) is a longer-term, recovery-focused relationship for participants living with psychosocial disability, focused on building skills, managing wellbeing, and slowly rebuilding the life you want. You can have both in some plans.",
      },
      {
        q: "Do I need to self-manage to work with Navica?",
        a: "No. We support participants who are NDIA-managed, plan-managed and self-managed. We invoice the appropriate party for the services you receive, you don't pay out of pocket for NDIS-funded supports.",
      },
      {
        q: "How do I switch coordinators to Navica?",
        a: "It is simpler than people think. You give your current provider 14 days notice in writing, send us a copy of your NDIS plan, and sign a Navica service agreement. We handle the handover with your previous coordinator and make sure nothing falls through the cracks.",
      },
      {
        q: "Do you also support DVA, DFV and athletes?",
        a: "Yes, these are growing areas of our practice. Our team includes AASW-accredited social workers, and we deliver DVA wellbeing coordination, social work for people affected by domestic and family violence (DFV), private social work, and athlete mental health, holistic social work focused on performance and recovery (our mind and motion work). These sit outside the NDIS price guide, so fees are agreed up front based on your situation. Get in touch via the Contact page and we'll walk you through options and pricing.",
      },
    ],
  },
  {
    id: "fees-funding",
    title: "Fees & funding",
    items: [
      {
        q: "What do you charge?",
        a: "We charge the published NDIS Pricing Arrangements rate for every support, never above. See the rate cards below for the exact 2026-27 rates in effect from 1 July 2026. All NDIS supports are GST-free.",
      },
      {
        q: "Will I pay anything out of pocket?",
        a: "No out-of-pocket cost for NDIS-funded supports. We bill the NDIA, your Plan Manager, or send you an invoice (if self-managed) at the published rate. Provider Travel may be charged separately and is capped at 30 minutes one-way in metro areas. Cancellations follow the NDIS Cancellation Rules.",
      },
      {
        q: "How does payment work under NDIA, Plan-Managed or Self-Managed?",
        a: "See the three payment types below, you don't need to know which one applies to you before reaching out; we'll work that out together at the discovery call.",
      },
      {
        q: "What about services outside the NDIS?",
        a: "Work outside the NDIS price guide (DVA wellbeing coordination, domestic and family violence support, athlete mental health, our mind and motion work, and private social work) is quoted based on scope. Get in touch via the Contact page and we'll walk you through options and fees before anything starts.",
      },
    ],
  },
  {
    id: "privacy-consent",
    title: "Privacy, consent & complaints",
    items: [
      {
        q: "Is my information safe?",
        a: "Yes. Navica complies with the NDIS Code of Conduct, NDIS Practice Standards (where applicable), the Australian Privacy Principles and the AASW Code of Ethics. Your personal and health information is stored securely, only accessed by team members supporting you, and never shared without your written consent, except where required by law (mandatory reporting).",
      },
      {
        q: "Can I bring a support person or advocate?",
        a: "Absolutely. You can bring a family member, friend, advocate or interpreter to any meeting. We can also organise an independent advocate through Disability Advocacy NSW (DA) at no cost to you.",
      },
      {
        q: "What if I'm not happy with the service?",
        a: "Talk to us first, we want to know. If something is not working we will adjust, change worker, or help you find a different provider with no hard feelings. You can also raise a complaint with the NDIS Quality and Safeguards Commission on 1800 035 544 at any time.",
      },
    ],
  },
];

export const FEE_TIERS = [
  {
    id: "support-coord-l2",
    badge: "Most common",
    name: "NDIS Support Coordination",
    code: "07_002_0106_8_3",
    rate: "$100.14",
    unit: "per hour",
    description: "Coordination of Supports, the day-to-day work of finding providers, navigating your plan, attending meetings, and keeping everything moving.",
    suitsLine: "Participants with Capacity Building, Support Coordination funded in their plan.",
  },
  {
    id: "psrc",
    name: "Psychosocial Recovery Coaching",
    code: "07_102_0136_5_3",
    rate: "$110.44",
    unit: "per hour",
    description: "Recovery-focused, longer-term support for people living with psychosocial disability. Rebuilding routine, managing wellbeing, working toward your life goals.",
    suitsLine: "Participants with Recovery Coaching funded under Capacity Building, Support Coordination.",
  },
  {
    id: "allied-health",
    name: "Social Work / Allied Health (Improved Daily Living)",
    code: "15_054_0128_1_3",
    rate: "$193.99",
    unit: "per hour",
    description: "AASW-accredited social work supports, assessments, counselling, capacity-building work, reports and letters of advocacy.",
    suitsLine: "Participants with Capacity Building, Improved Daily Living funded.",
  },
];

export const FUNDING_TYPES = [
  {
    id: "ndia",
    title: "NDIA managed (Agency managed)",
    body: "The NDIA pays Navica directly. You do nothing, we lodge claims through the NDIS portal at the published price guide rate. You never see an invoice.",
  },
  {
    id: "plan",
    title: "Plan managed",
    body: "A Plan Manager pays Navica on your behalf. We invoice your Plan Manager (we never bill above the NDIS price guide). You can choose your Plan Manager separately if you don't have one, we can recommend options.",
  },
  {
    id: "self",
    title: "Self managed",
    body: "You pay Navica directly from your NDIS funds, then claim back through the NDIS portal. We send you a clear invoice with the support item code on every line so claiming is quick.",
  },
];
